import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NoLabelForm } from '../components/forms/forms';
import {
  AddressBookDrawerBase,
  AddressBookDrawerListContent,
  AddressBookNoDataDialog,
} from '../dialogs/shipments/AddressBookDrawer';
import { FormItemSelect } from '../components/forms/selects';
import { useAccountOptions } from '../components/domainSpecific/accountElements';
import { useMountEffect } from '../common/utils/hookUtils';
import { useAddressBookLogic } from '../dialogs/shipments/useAddressBook';
import { DynamicFormDependenciesProvider } from '../components/forms/dynamic/dynamicFormDependencies';
import { useAccountElementsStyles } from '../components/domainSpecific/accountElements.styles';
import { useUserAddressBookStyles } from './UserAddressBook.styles';

export function SelectAccountDropdown({ setSelectedAccount, subTitleId }) {
  const { options: accountOptions = [] } = useAccountOptions();
  const { styles } = useAccountElementsStyles();
  const { styles: userAddressBookStyles } = useUserAddressBookStyles();

  useMountEffect(() => {
    if (accountOptions.length === 1) {
      setSelectedAccount(accountOptions[0]?.value);
    }
  }, [setSelectedAccount, accountOptions]);

  return (
    <div className={userAddressBookStyles.userAddressBook}>
      <p>
        <FormattedMessage id={subTitleId} />
      </p>
      <NoLabelForm
        onValuesChange={val => setSelectedAccount(val?.account)}
        className="no-margin-form-items"
      >
        <FormItemSelect
          name="account"
          labelId="book.newShipment.accountDialog.account"
          rules={[{ required: true }]}
          options={accountOptions}
          optionKey="number"
          formItemComponentProps={{
            classNames: { popup: { root: styles.accountSelectDropdown } },
          }}
        />
      </NoLabelForm>
    </div>
  );
}

export default function UserAddressBook({
  open,
  data,
  openDrawer,
  setData,
  onClose,
}) {
  const [selectedAccount, setSelectedAccount] = useState();
  const accountNumber = selectedAccount?.number;
  const isFormVisible = data?.editing || data?.adding;
  const readOnly = !selectedAccount;

  const {
    activeItem,
    setActiveItem,
    onCancel,
    onSave,
    REMOVE_PROPS,
    searchValue,
    searchOnChange,
    noDataDialog,
    openNoDataDialog,
    getEditInitialValues,
  } = useAddressBookLogic(accountNumber, setData, onClose);

  const addressBookDrawerBaseProps = {
    open,
    data,
    setData,
    onClose: () => {
      setSelectedAccount(undefined);
      onClose?.();
    },
    readOnly,
    isFormVisible,
    onCancel,
    onSave,
    REMOVE_PROPS,
    searchValue,
    searchOnChange,
    getEditInitialValues,
  };

  return (
    <DynamicFormDependenciesProvider values={{ accountNumber }}>
      <AddressBookDrawerBase
        {...addressBookDrawerBaseProps}
        isHeaderVisible={!!accountNumber}
      >
        {selectedAccount ? (
          <AddressBookDrawerListContent
            onEdit={val => setData({ editing: val })}
            onApply={({ id }) => {
              onClose();
              setActiveItem({ id });
            }}
            onClose={onClose}
            active={activeItem}
            isFormVisible={isFormVisible}
            openNoDataDialog={openNoDataDialog}
            searchValue={searchValue}
            readOnly={readOnly}
            {...REMOVE_PROPS}
          />
        ) : (
          <SelectAccountDropdown
            setSelectedAccount={setSelectedAccount}
            subTitleId="account.addressBook.subtitle"
          />
        )}
      </AddressBookDrawerBase>

      <AddressBookNoDataDialog
        onAdd={() => {
          openDrawer({ adding: true });
          noDataDialog.close();
        }}
        isOpen={noDataDialog.isOpen}
        onCancel={noDataDialog.close}
        readOnly={readOnly}
      />
    </DynamicFormDependenciesProvider>
  );
}
